// Fase 6A + Fix Billing + Trial: Context central de suscripción.
// Prioridad de planes: internalEntitlement > Google Play Billing > Trial Pro > Free
// La inicialización de Billing se omite si el usuario ya tiene internalEntitlement activo.

import React, { createContext, useContext, useState, useEffect } from 'react';
import { PLANS, getPermissionsForPlan } from '@/config/subscriptionPlans';
import { db } from '@/firebase';
import { onSnapshot, doc, collection } from 'firebase/firestore';
import { useAuth } from '@/context/AuthContext';
import {
  initBillingClient,
  querySubscriptionProducts,
  addPurchaseListener,
  mapPurchaseToPlan,
} from '@/services/billingService';
import { getTrialStatus } from '@/services/trialService';


const SubscriptionContext = createContext(null);

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error('useSubscription debe usarse dentro de <SubscriptionProvider>');
  }
  return ctx;
}

export function SubscriptionProvider({ children, workspaceId }) {
  const { user } = useAuth();
  const uid = user?.uid ?? null;

  // Plan inicial: siempre FREE. Se actualiza desde workspaceMetadata / Firestore entitlements.
  const [plan, _setPlan] = useState(PLANS.FREE);
  const [loadingSubscription] = useState(false); // Reservado para fase de integración real
  const [cachedProducts, setCachedProducts] = useState([]);
  const [billingPlan, setBillingPlan] = useState(null); // Plan desde Google Play Billing
   const [workspaceMetadataPlan, setWorkspaceMetadataPlan] = useState(null); // Plan desde workspaceMetadata effectivePlan
   const [entitlementPlan, setEntitlementPlan] = useState(null); // Plan desde internalEntitlements
   const [isPlanReady, setIsPlanReady] = useState(false); // true solo cuando workspaceMetadata+trial se resolvieron con dato real (no con error/timeout)
  // null = no iniciado o no aplica (workspaceMetadata cubre), true = Billing OK, false = Billing no disponible
  const [billingAvailable, setBillingAvailable] = useState(null);
  // Trial Pro 60 días
  const [trialPlan, setTrialPlan] = useState(null);         // null | PLANS.PREMIUM_PRO
  const [trialExpiresAt, setTrialExpiresAt] = useState(null); // Date | null
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(0); // número de días restantes


  // Testing: setPlan() disponible para testing manual desde DevTools
  const setPlan = (newPlan) => {
    _setPlan(newPlan);
  };

  // ── Calcular plan efectivo ──
  // Prioridad: workspaceMetadata (effectivePlan) > Google Play Billing > Trial Pro > Free
  useEffect(() => {
    let effectivePlan;

    if (workspaceMetadataPlan === PLANS.PREMIUM_PRO) {
      // 1. workspaceMetadata effectivePlan siempre gana
      effectivePlan = PLANS.PREMIUM_PRO;
    } else if (billingPlan === PLANS.PREMIUM_PRO) {
      // 2. Suscripción Google Play Pro
      effectivePlan = PLANS.PREMIUM_PRO;
    } else if (billingPlan === PLANS.PREMIUM_PERSONAL) {
      // 2b. Suscripción Google Play Personal
      effectivePlan = PLANS.PREMIUM_PERSONAL;
    } else if (trialPlan === PLANS.PREMIUM_PRO) {
      // 3. Trial Pro activo (solo si no hay billing activo)
      effectivePlan = PLANS.PREMIUM_PRO;
    } else {
      // 4. Fallback Free
      effectivePlan = PLANS.FREE;
    }

    _setPlan(effectivePlan);
  }, [billingPlan, workspaceMetadataPlan, trialPlan]);

  // ── Inicialización de Billing: se ejecuta UNA sola vez en toda la vida de la app ──
  // No depende de uid y no se reinicializa al cambiar de usuario (Billing es un cliente
  // global del dispositivo, no scoped a usuario Firebase).
  useEffect(() => {
    async function initBilling() {
      const result = await initBillingClient();
      if (!result.connected) {
        console.warn('[BILLING] BillingClient no conectado — Billing no disponible');
        setBillingAvailable(false);
        return;
      }

      setBillingAvailable(true);

      // Cachear productos para que PlanUpgradeModal tenga los offerTokens
      const products = await querySubscriptionProducts();
      setCachedProducts(products);

      // ⚠️ DESHABILITADO TEMPORALMENTE (Opción B):
      // No consultar compras activas automáticamente al arranque.
      // Razón: Google Play Billing devuelve compras de la cuenta Google del dispositivo,
      // no scoped a usuario Firebase. Esto causa que una suscripción activa del propietario
      // se aplique a cualquier cuenta Firebase nueva en el mismo Android.
      //
      // Solución temporal: Las cuentas nuevas permanecerán en Free.
      // El propietario usa internal entitlement Firestore (internalEntitlements/{uid}).
      // Usuarios de pago pueden usar "Restaurar compra" manualmente si es necesario.
      //
      // Antes de lanzamiento público, se implementará Firestore binding compra ↔ Firebase UID (Opción A).
      //
      // const purchases = await queryActivePurchases();
      // for (const purchase of purchases) {
      //   const derivedPlan = mapPurchaseToPlan(purchase);
      //   if (derivedPlan) {
      //     setBillingPlan(derivedPlan);
      //     break;
      //   }
      // }

      // Listener para cuando el usuario completa una compra en tiempo real
      addPurchaseListener((data) => {
        if (data.responseCode === 0 && data.purchases?.length > 0) {
          for (const purchase of data.purchases) {
            const newPlan = mapPurchaseToPlan(purchase);
            if (newPlan) {
              setBillingPlan(newPlan);
              break;
            }
          }
        }
      });
    }
    initBilling();
  }, []);

  // ── Entitlement + Trial: dependiente de uid (reactivo vía useAuth) ──
  // Al cambiar uid:
  //  - se cancela el listener/promesas del usuario anterior (flag `cancelled`)
  //  - se limpia entitlementPlan/trialPlan del usuario anterior
  //  - isPlanReady vuelve a false hasta que ambas fuentes resuelvan con dato real
  //  - un error controlado marca la fuente como "resuelta" para no bloquear el gate
  //    indefinidamente, PERO nunca deja entitlementPlan en un estado que aparente
  //    "Free confirmado": el error se loguea y entitlementPlan queda null (igual que
  //    "no entitlement"), que es el único valor seguro cuando no se pudo confirmar.
  useEffect(() => {
    let cancelled = false;

    // Reset inmediato del usuario anterior
    setEntitlementPlan(null);
    setTrialPlan(null);
    setTrialExpiresAt(null);
    setTrialDaysRemaining(0);
    setIsPlanReady(false);

    if (!uid) {
      // Sin usuario autenticado no hay nada que resolver: ready sin entitlement posible
      setIsPlanReady(true);
      return;
    }

    let entitlementResolved = false;
    let trialResolved = false;
    const maybeReady = () => {
      if (entitlementResolved && trialResolved && !cancelled) {
        setIsPlanReady(true);
      }
    };

    getTrialStatus(uid)
      .then((trialStatus) => {
        if (cancelled) return;
        if (trialStatus.hasActiveTrial) {
          setTrialPlan(PLANS.PREMIUM_PRO);
          setTrialExpiresAt(trialStatus.trialExpiresAt);
          setTrialDaysRemaining(trialStatus.daysRemaining);
        }
        trialResolved = true;
        maybeReady();
      })
      .catch((trialErr) => {
        console.warn('[TRIAL] Error checking trial status:', trialErr?.message);
        trialResolved = true; // error controlado: no bloquea el gate indefinidamente
        maybeReady();
      });

    const unsubscribe = onSnapshot(
      doc(collection(db, 'internalEntitlements'), uid),
      (docSnap) => {
        if (cancelled) return;
        if (docSnap.exists()) {
          const data = docSnap.data();
          setEntitlementPlan(
            data.active === true && data.plan === PLANS.PREMIUM_PRO ? PLANS.PREMIUM_PRO : null
          );
        } else {
          setEntitlementPlan(null);
        }
        entitlementResolved = true;
        maybeReady();
      },
      (error) => {
        if (cancelled) return;
        // Error real de Firestore: NO se puede confirmar el entitlement.
        // entitlementResolved permanece false a propósito para no marcar isPlanReady=true
        // con un estado no confirmado (evita volver a mostrar "Free" como si fuera dato bueno).
        console.error('[ENTITLEMENT] Error loading internal entitlement:', error);
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [uid]);

  // ── WorkspaceMetadata: leer effectivePlan desde workspaceMetadata/{workspaceId} ──
  // Al cambiar workspaceId:
  //  - se cancela el listener anterior
  //  - se limpia workspaceMetadataPlan
  //  - si workspaceId es null, se cancela y restablece a null
  useEffect(() => {
    let cancelled = false;

    setWorkspaceMetadataPlan(null);

    if (!workspaceId) {
      return;
    }

    const unsubscribe = onSnapshot(
      doc(collection(db, 'workspaceMetadata'), workspaceId),
      (docSnap) => {
        if (cancelled) return;
        if (docSnap.exists()) {
          const data = docSnap.data();
          const effectivePlan = data.effectivePlan;
          setWorkspaceMetadataPlan(
            effectivePlan === 'PREMIUM_PRO' ? PLANS.PREMIUM_PRO : null
          );
        } else {
          setWorkspaceMetadataPlan(null);
        }
      },
      (error) => {
        if (cancelled) return;
        console.error('[WORKSPACE_METADATA] Error loading workspace metadata:', error);
        // Error real: workspaceMetadataPlan queda null (seguro)
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [workspaceId]);

  // ── Restaurar compras ──
  const restorePurchases = async () => {
    const { restorePurchases: billingRestore } = await import('@/services/billingService');
    const restoredPlan = await billingRestore();
    setPlan(restoredPlan);
    return restoredPlan;
  };

  const permissions = getPermissionsForPlan(plan);

  const value = {
    plan,
    isFree:             plan === PLANS.FREE,
    isPremiumPersonal:  plan === PLANS.PREMIUM_PERSONAL,
    isPremiumPro:       plan === PLANS.PREMIUM_PRO,
    loadingSubscription,
    setPlan,            // ⚠️ Solo para testing interno - se eliminará en fase de integración real
    permissions,        // Objeto con todos los feature flags del plan actual
    cachedProducts,     // ← para que PlanUpgradeModal lea offerTokens reales
    restorePurchases,   // ← para botón "Restaurar compra"
    isPlanReady,        // ← true cuando el plan esté resuelto (internal entitlement chequeado)
    billingAvailable,   // ← null = no aplica (entitlement cubre), true = Billing OK, false = no disponible
    trialPlan,          // ← null | PLANS.PREMIUM_PRO si trial activo
    trialExpiresAt,     // ← Date | null con fecha de expiración del trial
    trialDaysRemaining, // ← número de días restantes del trial
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}
